import { useState } from "react";
import { useParams } from "react-router-dom";
import { Link } from "@/components/site/LocaleLink";
import { motion } from "framer-motion";
import { ArrowLeft, MessageCircle, Send, FlaskConical, Bird, Syringe, Snowflake, Factory, Tag, FileText } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Picture } from "@/components/site/Picture";
import { ProductCard } from "@/components/site/ProductCard";
import { Reveal, RevealGroup, RevealItem } from "@/components/site/Reveal";
import { useLang, useL } from "@/context/LanguageContext";
import { useSeo, SITE_URL } from "@/hooks/useSeo";
import { getProduct, getCategory, PRODUCTS, SPECIES } from "@/data/products";
import { imgSrc } from "@/data/images";
import { CONTACT, WHATSAPP_ENABLED } from "@/data/copy";
import { withLang } from "@/lib/lang";
import NotFound from "@/pages/NotFound";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const isRtl = lang === "ar";
  const L = useL();
  const product = getProduct(slug);
  const [active, setActive] = useState(0);

  const cat = product ? getCategory(product.category) : null;

  // When the slug is unknown we render <NotFound />, which owns the page title
  // and the noindex tag — so leave those alone here rather than fighting it.
  useSeo({
    title: product ? `${L(product.name)} | Al-Abrad` : undefined,
    description: product ? L(product.description) : undefined,
    image: product ? imgSrc(product.image) : undefined,
    noIndex: !product,
    jsonLd: product
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Product",
              name: L(product.name),
              description: L(product.description),
              image: `${SITE_URL}${imgSrc(product.image)}`,
              category: L(cat.name),
              brand: { "@type": "Brand", name: product.manufacturer },
              manufacturer: { "@type": "Organization", name: product.manufacturer },
              url: `${SITE_URL}${withLang(lang, `/products/${product.slug}`)}`,
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: t.nav.home, item: `${SITE_URL}${withLang(lang, "/")}` },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: t.nav.products,
                  item: `${SITE_URL}${withLang(lang, "/products")}`,
                },
                { "@type": "ListItem", position: 3, name: L(product.name) },
              ],
            },
          ],
        }
      : undefined,
  });

  if (!product) return <NotFound />;

  const related = PRODUCTS.filter((p) => p.category === product.category && p.slug !== product.slug).slice(0, 3);
  const speciesNames = product.species
    .map((id) => SPECIES.find((s) => s.id === id))
    .filter(Boolean)
    .map((s) => L(s.name));

  const quoteMsg = isRtl
    ? `مرحباً، أرغب بعرض سعر لمنتج: ${product.name.ar}`
    : `Hello, I'd like a quote for: ${product.name.en}`;
  // With WhatsApp switched off the quote button sends people to the contact
  // form instead, carrying the product name across so they don't retype it.
  const quoteHref = WHATSAPP_ENABLED
    ? `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(quoteMsg)}`
    : `/contact?product=${encodeURIComponent(L(product.name))}`;

  const quoteBtnClass =
    "group flex w-full items-center justify-center gap-2 rounded-full bg-forest px-6 py-4 text-sm font-semibold text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-forest-dark";

  const specs = [
    { icon: FlaskConical, label: t.detail.composition, value: L(product.composition) },
    { icon: Syringe, label: t.detail.dosage, value: L(product.dosage) },
    { icon: Snowflake, label: t.detail.storage, value: L(product.storage) },
    { icon: Factory, label: t.detail.manufacturer, value: product.manufacturer },
  ];

  return (
    <>
      <PageHero title={L(product.name)} crumb={t.nav.products} image={product.image} />

      <section className="bg-cream py-16 lg:py-24">
        <div className="container-x">
          <Link
            to="/products"
            data-testid="back-to-products"
            className="mb-10 inline-flex items-center gap-2 text-sm font-semibold text-forest transition-colors hover:text-gold-ink"
          >
            <ArrowLeft className="h-4 w-4 rtl-flip" />
            {t.detail.back}
          </Link>

          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            {/* gallery */}
            <div>
              <motion.div
                key={active}
                initial={{ opacity: 0.4, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="relative overflow-hidden rounded-2xl border border-line bg-gradient-to-b from-sage/40 to-mint"
                data-testid="product-gallery-main"
              >
                <Picture
                  img={product.gallery[active]}
                  alt={L(product.name)}
                  className="aspect-square w-full object-cover"
                  sizes="(min-width: 1024px) 50vw, 100vw"
                />
              </motion.div>
              {product.gallery.length > 1 && (
              <div className="mt-4 flex gap-3">
                {product.gallery.map((shot, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActive(i)}
                    data-testid={`gallery-thumb-${i}`}
                    aria-label={`${t.detail.viewImage} ${i + 1}`}
                    aria-current={active === i ? "true" : undefined}
                    className={`relative h-20 w-20 overflow-hidden rounded-lg border-2 transition-colors ${
                      active === i ? "border-forest" : "border-line hover:border-forest/40"
                    }`}
                  >
                    <Picture img={shot} alt="" className="h-full w-full object-cover" sizes="80px" />
                  </button>
                ))}
              </div>
              )}
            </div>

            {/* info */}
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-sage px-3 py-1 text-xs font-semibold text-forest">
                  <Tag className="h-3 w-3" /> {L(cat.name)}
                </span>
                <span className="rounded-full border border-gold/40 px-3 py-1 text-xs font-semibold text-gold-ink">
                  {L(product.tag)}
                </span>
              </div>
              <h2 className="mt-5 font-display text-3xl font-extrabold leading-tight tracking-tight text-forest sm:text-4xl">
                {L(product.name)}
              </h2>
              <p className="mt-5 text-base leading-relaxed text-stone">{L(product.description)}</p>

              {/* target species */}
              <div className="mt-8">
                <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone">
                  <Bird className="h-4 w-4 text-gold" /> {t.detail.species}
                </p>
                <div className="flex flex-wrap gap-2">
                  {speciesNames.map((s, i) => (
                    <span key={i} className="rounded-full bg-white border border-line px-4 py-1.5 text-sm text-ink">
                      {s}
                    </span>
                  ))}
                </div>
              </div>

              {/* quote */}
              <div className="mt-8 rounded-2xl border border-line bg-white p-6">
                {WHATSAPP_ENABLED ? (
                  <a
                    href={quoteHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="request-quote"
                    className={quoteBtnClass}
                  >
                    <MessageCircle className="h-5 w-5" />
                    {t.detail.quote}
                  </a>
                ) : (
                  <Link to={quoteHref} data-testid="request-quote" className={quoteBtnClass}>
                    <Send className="h-5 w-5 rtl-flip" />
                    {t.detail.quote}
                  </Link>
                )}
                {product.brochure && (
                  <a
                    href={product.brochure}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="download-brochure"
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-forest/25 bg-white px-6 py-3.5 text-sm font-semibold text-forest transition-all duration-300 hover:-translate-y-0.5 hover:border-forest hover:bg-sage/40"
                  >
                    <FileText className="h-5 w-5" />
                    {t.detail.brochure}
                  </a>
                )}
                <p className="mt-3 text-center text-xs text-stone">{t.detail.note}</p>
              </div>
            </div>
          </div>

          {/* specs */}
          <RevealGroup className="mt-16 grid gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-2">
            {specs.map((spec, i) => {
              const Icon = spec.icon;
              return (
                <RevealItem key={i} className="bg-white p-7" data-testid={`spec-${i}`}>
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gold-ink">
                    <Icon className="h-4 w-4" /> {spec.label}
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-ink">{spec.value}</p>
                </RevealItem>
              );
            })}
          </RevealGroup>

          {/* related */}
          {related.length > 0 && (
            <div className="mt-20">
              <Reveal>
                <h3 className="mb-8 font-display text-2xl font-extrabold text-forest sm:text-3xl">{t.detail.related}</h3>
              </Reveal>
              <RevealGroup className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((p) => (
                  <RevealItem key={p.slug}>
                    <ProductCard product={p} />
                  </RevealItem>
                ))}
              </RevealGroup>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
